import type { MenuItemType } from "@/common/types/menu";
import {
  ANALYTICS_PATH,
  SHIPPED_ANALYTICS_CHILDREN,
  type AnalyticsChild,
} from "@/pages/analytics/analyticsRoutes";

/**
 * The Analytics group: one plain child per analytics screen passed in, in that order
 * (ANL-E04-S03). The sidebar passes only the shipped ones, so a screen appears when its route
 * ships, with no placeholder entries.
 */
export const buildAnalyticsMenuItem = (children: readonly AnalyticsChild[]): MenuItemType => ({
  key: "analytics",
  label: "Analytics",
  icon: "analytics",
  children: children.map((child) => ({
    key: `analytics-${child.path.slice(ANALYTICS_PATH.length + 1)}`,
    label: child.label,
    url: child.path,
    parentKey: "analytics",
  })),
});

/**
 * Sidebar navigation, grouped by what an operator is doing rather than by data
 * model. Every section that has a single destination is a direct link: the list
 * screens carry their own "Add" button, so a "List / Create" dropdown only added
 * a click. Taxonomy keeps children because it has genuinely separate lists, and
 * Analytics because its screens ship one by one.
 */
export const MENU_ITEMS: MenuItemType[] = [
  {
    key: "overview",
    label: "Overview",
    isTitle: true,
  },
  {
    key: "dashboard",
    label: "Dashboard",
    icon: "dashboard",
    url: "/dashboard",
  },
  buildAnalyticsMenuItem(SHIPPED_ANALYTICS_CHILDREN),
  {
    key: "growth",
    label: "Growth",
    isTitle: true,
  },
  {
    key: "commerce-media",
    label: "Commerce Media",
    icon: "megaphone",
    url: "/commerce-media/campaigns",
  },
  {
    key: "intelligence",
    label: "Intelligence",
    icon: "intelligence",
    url: "/intelligence",
  },
  {
    key: "gnn-operations",
    label: "GNN Operations",
    icon: "graph",
    url: "/gnn",
  },
  {
    key: "agentic-checkout",
    label: "Agentic Checkout",
    icon: "cart-check",
    url: "/agentic-checkout",
  },
  {
    key: "catalog",
    label: "Catalog",
    isTitle: true,
  },
  {
    key: "holidays",
    label: "Holidays",
    icon: "calendar",
    url: "/holidays",
  },
  {
    key: "taxonomy",
    label: "Taxonomy",
    icon: "layers",
    children: [
      {
        key: "taxonomy-collections",
        label: "Collections",
        url: "/taxonomy/collections",
        parentKey: "taxonomy",
      },
      {
        key: "taxonomy-categories",
        label: "Categories",
        url: "/taxonomy/categories",
        parentKey: "taxonomy",
      },
      {
        key: "taxonomy-relationships",
        label: "Relationships",
        url: "/taxonomy/relationships",
        parentKey: "taxonomy",
      },
    ],
  },
  {
    key: "affiliate-offers",
    label: "Affiliate offers",
    icon: "tag",
    url: "/offers",
  },
  {
    key: "brands",
    label: "Brands",
    icon: "tag",
    url: "/brands",
  },
  {
    key: "community",
    label: "Community",
    isTitle: true,
  },
  {
    key: "users",
    label: "Users",
    icon: "users",
    url: "/users",
  },
  {
    key: "orders",
    label: "Orders",
    icon: "receipt",
    url: "/orders",
  },
  {
    key: "badges",
    label: "Badges",
    icon: "award",
    url: "/badges",
  },
  {
    key: "announcements",
    label: "Announcements",
    icon: "bell",
    url: "/announcements",
  },
  {
    key: "feedbacks",
    label: "Feedbacks",
    icon: "message",
    url: "/feedbacks",
  },
  {
    key: "charityOrganizations",
    label: "Charity Organizations",
    icon: "heart",
    url: "/charity-organizations",
  },
  {
    key: "charityBookkeeping",
    label: "Charity Bookkeeping",
    icon: "book",
    url: "/charity-bookkeeping",
  },
  {
    key: "system",
    label: "Settings",
    isTitle: true,
  },
  {
    key: "company-info",
    label: "Company Information",
    icon: "building",
    url: "/company-info",
  },
  {
    key: "gyftcoin-setting",
    label: "Sparks Settings",
    icon: "sliders",
    url: "/gyftcoin-setting",
  },
  {
    key: "legal-documents",
    label: "Legal Documents",
    icon: "book",
    url: "/legal-documents",
  },
];
